<script>
import { mapGetters } from 'vuex';
import { format } from 'date-fns';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ConversationsExportAPI from 'dashboard/api/conversationsExport';

const CONVERSATION_STATUSES = [
  { id: 'all', label: 'Todos' },
  { id: 'open', label: 'Abierta' },
  { id: 'resolved', label: 'Resuelta' },
  { id: 'pending', label: 'Pendiente' },
  { id: 'snoozed', label: 'Pospuesta' },
];

const DEAL_STATUSES = [
  { id: 'open', label: 'Abierto' },
  { id: 'won', label: 'Ganado' },
  { id: 'lost', label: 'Perdido' },
  { id: 'canceled', label: 'Cancelado' },
  { id: 'paused', label: 'Pausado' },
];

const FORMAT_OPTIONS = [
  { id: 'jsonl', label: 'JSONL (recomendado)' },
  { id: 'csv', label: 'CSV' },
];

export default {
  name: 'ConversationsExportModal',
  components: {
    WootDateRangePicker,
    V4Button,
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      createdAtRange: [null, null],
      lastActivityAtRange: [null, null],
      dateOperator: 'and',
      selectedAgents: [],
      selectedInbox: null,
      selectedTeam: null,
      selectedStatus: CONVERSATION_STATUSES[0],
      selectedDealStatus: null,
      selectedFormat: FORMAT_OPTIONS[0],
      conversationStatuses: CONVERSATION_STATUSES,
      dealStatuses: DEAL_STATUSES,
      formatOptions: FORMAT_OPTIONS,
      matchCount: null,
      countLoading: false,
      exportLimit: null,
    };
  },
  computed: {
    ...mapGetters({
      agentOptions: 'agents/getAgents',
      inboxOptions: 'inboxes/getInboxes',
      teamOptions: 'teams/getTeams',
    }),
    currentFilters() {
      return this.buildFilters();
    },
    hasCreatedAtFilter() {
      return !!(this.createdAtRange[0] || this.createdAtRange[1]);
    },
    hasLastActivityAtFilter() {
      return !!(this.lastActivityAtRange[0] || this.lastActivityAtRange[1]);
    },
    showDateOperator() {
      return this.hasCreatedAtFilter && this.hasLastActivityAtFilter;
    },
    canExport() {
      if (this.matchCount === null) return true;
      if (this.matchCount === 0) return false;
      if (this.exportLimit && this.matchCount > this.exportLimit) return false;
      return true;
    },
  },
  watch: {
    currentFilters: {
      handler() {
        this.debouncedFetchCount();
      },
      deep: true,
    },
  },
  created() {
    this.debouncedFetchCount = debounce(() => this.fetchCount(), 500);
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('teams/get');
  },
  methods: {
    open() {
      this.isOpen = true;
      this.fetchCount();
    },
    close() {
      this.isOpen = false;
    },
    async fetchCount() {
      this.countLoading = true;
      try {
        const { data } = await ConversationsExportAPI.count(
          this.buildFilters()
        );
        this.matchCount = data.count;
        this.exportLimit = data.limit;
      } catch (error) {
        this.matchCount = null;
      } finally {
        this.countLoading = false;
      }
    },
    handleCreatedAtRange(range) {
      this.createdAtRange = range;
    },
    handleLastActivityAtRange(range) {
      this.lastActivityAtRange = range;
    },
    buildFilters() {
      const filters = {
        export_format: this.selectedFormat.id,
      };

      if (this.createdAtRange[0]) {
        filters.created_at_from = format(this.createdAtRange[0], 'yyyy-MM-dd');
      }
      if (this.createdAtRange[1]) {
        filters.created_at_to = format(this.createdAtRange[1], 'yyyy-MM-dd');
      }
      if (this.lastActivityAtRange[0]) {
        filters.last_activity_at_from = format(
          this.lastActivityAtRange[0],
          'yyyy-MM-dd'
        );
      }
      if (this.lastActivityAtRange[1]) {
        filters.last_activity_at_to = format(
          this.lastActivityAtRange[1],
          'yyyy-MM-dd'
        );
      }
      if (this.showDateOperator) {
        filters.date_operator = this.dateOperator;
      }
      if (this.selectedAgents.length > 0) {
        filters.assignee_ids = this.selectedAgents.map(a => a.id);
      }
      if (this.selectedInbox) {
        filters.inbox_id = this.selectedInbox.id;
      }
      if (this.selectedTeam) {
        filters.team_id = this.selectedTeam.id;
      }
      if (this.selectedStatus && this.selectedStatus.id !== 'all') {
        filters.status = this.selectedStatus.id;
      }
      if (this.selectedDealStatus) {
        filters.deal_status = this.selectedDealStatus.id;
      }

      return filters;
    },
    async handleExport() {
      this.loading = true;
      try {
        await ConversationsExportAPI.export(this.buildFilters());
        useAlert(this.$t('CONVERSATIONS_EXPORT.SUCCESS'));
        this.close();
      } catch (error) {
        const serverError = error?.response?.data?.error;
        if (error?.response?.status === 422 && serverError) {
          useAlert(serverError);
        } else {
          useAlert(this.$t('CONVERSATIONS_EXPORT.ERROR'));
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 flex items-start justify-center pt-10 pb-6"
        style="z-index: 9998"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-[4px]"
          @click="close"
        />

        <!-- Panel -->
        <div
          class="relative w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-xl shadow-xl flex flex-col gap-5 p-6"
          style="z-index: 9999"
        >
          <!-- Header -->
          <div class="flex flex-col gap-2">
            <h3 class="text-base font-medium leading-6 text-n-slate-12">
              {{ $t('CONVERSATIONS_EXPORT.HEADER') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t('CONVERSATIONS_EXPORT.DESCRIPTION') }}
            </p>
          </div>

          <!-- Fields -->
          <div class="flex flex-col gap-5 max-h-[60vh] overflow-y-auto pl-1 pr-1">
            <!-- Formato -->
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.FORMAT') }}
                <span class="text-red-500">*</span>
              </label>
              <div class="multiselect-wrap--small">
                <multiselect
                  v-model="selectedFormat"
                  class="no-margin"
                  :options="formatOptions"
                  track-by="id"
                  label="label"
                  :searchable="false"
                  :allow-empty="false"
                  :show-labels="false"
                />
              </div>
            </div>

            <!-- Fecha creación -->
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.CREATED_AT') }}
              </label>
              <WootDateRangePicker
                :value="createdAtRange"
                :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
                :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
                @change="handleCreatedAtRange"
              />
            </div>

            <!-- Operador entre rangos de fecha -->
            <div v-if="showDateOperator" class="flex items-center gap-2 -my-2">
              <span class="text-xs text-n-slate-11">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.DATE_OPERATOR') }}
              </span>
              <div class="inline-flex overflow-hidden border rounded-md border-n-weak">
                <button
                  type="button"
                  class="px-2 py-0.5 text-xs font-medium"
                  :class="
                    dateOperator === 'and'
                      ? 'bg-n-brand text-white'
                      : 'text-n-slate-11 hover:bg-n-slate-3'
                  "
                  @click="dateOperator = 'and'"
                >
                  {{ $t('CONVERSATIONS_EXPORT.FILTERS.DATE_OPERATOR_AND') }}
                </button>
                <button
                  type="button"
                  class="px-2 py-0.5 text-xs font-medium border-l border-n-weak"
                  :class="
                    dateOperator === 'or'
                      ? 'bg-n-brand text-white'
                      : 'text-n-slate-11 hover:bg-n-slate-3'
                  "
                  @click="dateOperator = 'or'"
                >
                  {{ $t('CONVERSATIONS_EXPORT.FILTERS.DATE_OPERATOR_OR') }}
                </button>
              </div>
            </div>

            <!-- Fecha última actividad -->
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.LAST_ACTIVITY_AT') }}
              </label>
              <WootDateRangePicker
                :value="lastActivityAtRange"
                :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
                :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
                @change="handleLastActivityAtRange"
              />
            </div>

            <!-- Estado conversación -->
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.STATUS') }}
              </label>
              <div class="multiselect-wrap--small">
                <multiselect
                  v-model="selectedStatus"
                  class="no-margin"
                  :options="conversationStatuses"
                  track-by="id"
                  label="label"
                  :searchable="false"
                  :allow-empty="false"
                  :show-labels="false"
                />
              </div>
            </div>

            <!-- Agentes -->
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.AGENTS') }}
              </label>
              <div class="multiselect-wrap--small">
                <multiselect
                  v-model="selectedAgents"
                  class="no-margin"
                  :options="agentOptions"
                  track-by="id"
                  label="name"
                  multiple
                  :close-on-select="false"
                  :clear-on-select="false"
                  hide-selected
                  :placeholder="$t('CONVERSATIONS_EXPORT.FILTERS.AGENTS_PLACEHOLDER')"
                  :show-labels="false"
                />
              </div>
            </div>

            <!-- Inbox -->
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.INBOX') }}
              </label>
              <div class="multiselect-wrap--small">
                <multiselect
                  v-model="selectedInbox"
                  class="no-margin"
                  :options="inboxOptions"
                  track-by="id"
                  label="name"
                  :placeholder="$t('CONVERSATIONS_EXPORT.FILTERS.INBOX_PLACEHOLDER')"
                  :show-labels="false"
                />
              </div>
            </div>

            <!-- Equipo -->
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.TEAM') }}
              </label>
              <div class="multiselect-wrap--small">
                <multiselect
                  v-model="selectedTeam"
                  class="no-margin"
                  :options="teamOptions"
                  track-by="id"
                  label="name"
                  :placeholder="$t('CONVERSATIONS_EXPORT.FILTERS.TEAM_PLACEHOLDER')"
                  :show-labels="false"
                />
              </div>
            </div>

            <!-- Estado de deal CRM -->
            <div class="flex flex-col gap-1 pb-12">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATIONS_EXPORT.FILTERS.DEAL_STATUS') }}
              </label>
              <div class="multiselect-wrap--small">
                <multiselect
                  v-model="selectedDealStatus"
                  class="no-margin"
                  :options="dealStatuses"
                  track-by="id"
                  label="label"
                  :placeholder="$t('CONVERSATIONS_EXPORT.FILTERS.DEAL_STATUS_PLACEHOLDER')"
                  :show-labels="false"
                />
              </div>
            </div>
          </div>

          <!-- Resultado del conteo -->
          <div class="flex flex-col gap-1">
            <p v-if="countLoading" class="mb-0 text-sm text-n-slate-11">
              {{ $t('CONVERSATIONS_EXPORT.COUNT_LOADING') }}
            </p>
            <p v-else-if="matchCount === 0" class="mb-0 text-sm text-n-amber-12">
              {{ $t('CONVERSATIONS_EXPORT.COUNT_ZERO') }}
            </p>
            <p
              v-else-if="exportLimit && matchCount > exportLimit"
              class="mb-0 text-sm text-n-ruby-11"
            >
              {{
                $t('CONVERSATIONS_EXPORT.COUNT_TOO_MANY', {
                  count: matchCount,
                  max: exportLimit,
                })
              }}
            </p>
            <p v-else-if="matchCount !== null" class="mb-0 text-sm text-n-slate-11">
              {{ $t('CONVERSATIONS_EXPORT.COUNT_RESULT', { count: matchCount }) }}
            </p>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between w-full gap-3">
            <V4Button
              variant="faded"
              color="slate"
              :label="$t('DIALOG.BUTTONS.CANCEL')"
              class="w-full"
              type="button"
              @click="close"
            />
            <V4Button
              color="blue"
              icon="i-ph-download-simple"
              :label="$t('CONVERSATIONS_EXPORT.EXPORT_BUTTON')"
              class="w-full"
              :is-loading="loading"
              :disabled="!canExport"
              type="button"
              @click="handleExport"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
